import React from 'react';
import {
  TelemetryPanelConfig,
  TelemetryUnitSystem,
  TelemetryViewportState,
} from '../types/telemetry-dashboard.types';
import {
  latestAtViewport,
  conv,
  unitOf,
  getChannelDef,
  bundle,
  normalizePanelParameter,
} from '../utils/telemetry-helpers.utils';
import { cleanUnit, isNoneUnit } from '../utils/telemetry-unit.utils';
import { resolveEnumValue, isBooleanEnumState } from '../utils/telemetry-enum.utils';
import '../styles/telemetry-dashboard-panel.css';

export interface TelemetryCardKpiProps {
  panel?: TelemetryPanelConfig;
  timeframe?: number;
  precision?: number;
  units?: TelemetryUnitSystem;
  viewport?: TelemetryViewportState;
  valStr?: string;
  unitStr?: string;
  diffStr?: string;
  diffColor?: string;
  diffIcon?: string;
  minStr?: string;
  maxStr?: string;
  sparklineId?: string;
  className?: string;
  style?: React.CSSProperties;
}

export const TelemetryCardKpi: React.FC<TelemetryCardKpiProps> = ({
  panel,
  timeframe = 5,
  precision = 1,
  units = {},
  viewport = { spanMs: 300000, offsetMs: 0 },
  valStr: overrideValStr,
  unitStr: overrideUnitStr,
  diffStr: overrideDiffStr,
  diffColor: overrideDiffColor,
  diffIcon: overrideDiffIcon,
  minStr: overrideMinStr,
  maxStr: overrideMaxStr,
  sparklineId: overrideSparklineId,
  className = '',
  style,
}) => {
  let valStr = overrideValStr || '0.0';
  let unitStr = overrideUnitStr || '';
  let diffStr = overrideDiffStr || '';
  let diffColor = overrideDiffColor || 'rgb(193, 229, 91)';
  let diffIcon = overrideDiffIcon || '';
  let minStr = overrideMinStr || '--';
  let maxStr = overrideMaxStr || '--';
  let sparkId = overrideSparklineId || (panel ? `sp:${panel.id}` : '');
  let enumState: 'on' | 'off' | null = null;
  let rawValForEnum: number | string | null = null;
  let enumValText: string | null = null;

  if (panel) {
    const first = panel.parameters?.[0];
    const def = first ? normalizePanelParameter(first) : getChannelDef();
    const val = latestAtViewport(panel as any, viewport);
    const enumVal = resolveEnumValue(val, def?.enumValues);

    if (enumVal) {
      enumValText = enumVal;
      valStr = overrideValStr ?? enumValText;
      enumState = isBooleanEnumState(enumValText);
      rawValForEnum = val;
      unitStr = overrideUnitStr ?? '';
      diffStr = overrideDiffStr ?? '';
    } else {
      const convertedVal = conv(val, def, units as any);
      unitStr = cleanUnit(overrideUnitStr ?? (unitOf(def, units as any) || def.unit || ''));
      valStr = overrideValStr ?? (typeof convertedVal === 'number' ? convertedVal.toFixed(precision) : String(convertedVal || '0.0'));

      const b = bundle(panel as any, timeframe, viewport);
      const primarySeries = b.series?.[0];
      const seriesPoints: number[] = (primarySeries?.data || [])
        .map((v: any) => conv(typeof v === 'number' ? v : v?.value?.[1] ?? v, def, units as any))
        .filter((v: any): v is number => typeof v === 'number' && v !== null && !isNaN(v));

      const fallbackNum = typeof convertedVal === 'number' && convertedVal !== null ? convertedVal : 0;
      const minVal = seriesPoints.length > 0 ? Math.min(...seriesPoints) : fallbackNum;
      const maxVal = seriesPoints.length > 0 ? Math.max(...seriesPoints) : fallbackNum;
      minStr = overrideMinStr ?? minVal.toFixed(precision);
      maxStr = overrideMaxStr ?? maxVal.toFixed(precision);

      const currPt = seriesPoints.length > 0 ? (seriesPoints[seriesPoints.length - 1] ?? fallbackNum) : fallbackNum;
      const prevPt = seriesPoints.length >= 2 ? (seriesPoints[seriesPoints.length - 2] ?? currPt) : currPt;
      const diff = currPt - prevPt;
      const diffSign = diff > 0 ? '+' : '';
      diffStr = overrideDiffStr ?? `${diffSign}${diff.toFixed(precision)}`;
      diffColor = overrideDiffColor ?? (diff < 0 ? '#FF6A6A' : '#CCF54E');
      diffIcon = overrideDiffIcon ?? (diff < 0 ? '▼' : '▲');
    }
  }

  const panelId = panel?.id || 'default';

  return (
    <div id={`telemetry-kpi-container-${panelId}`} className={`telemetry-kpi-container ${className}`} style={style}>
      {/* Top Main Value Section */}
      <div className="telemetry-kpi-val-row">
        {enumState ? (
          <div style={{ display: 'inline-flex', alignItems: 'center' }}>
            <span id={`telemetry-kpi-value-${panelId}`} className={`td-enum-badge td-enum-badge--kpi td-enum-badge--${enumState}`}>
              {enumState.toUpperCase()}
            </span>
            {rawValForEnum != null && (
              <span className="td-enum-kpi-raw">({rawValForEnum})</span>
            )}
          </div>
        ) : enumValText ? (
          <span id={`telemetry-kpi-value-${panelId}`} className="telemetry-kpi-value">
            {enumValText} {rawValForEnum != null && <span className="td-enum-kpi-raw">({rawValForEnum})</span>}
          </span>
        ) : (
          <span id={`telemetry-kpi-value-${panelId}`} className="telemetry-kpi-value">{valStr}</span>
        )}
        {!isNoneUnit(unitStr) && <span id={`telemetry-kpi-unit-${panelId}`} className="telemetry-kpi-unit">{unitStr}</span>}
      </div>

      {/* Second Row: vs prev Delta */}
      {diffStr && (
        <div id={`telemetry-kpi-delta-${panelId}`} className="telemetry-kpi-delta" style={{ color: diffColor }}>
          <span style={{ fontSize: 10 }}>{diffIcon}</span>
          <span style={{ fontWeight: 400 }}>{diffStr}</span>
          <span style={{ color: '#7B7B7B', marginLeft: 2, fontFamily: 'var(--font-body, sans-serif)', fontSize: 11, fontWeight: 400 }}>vs prev</span>
        </div>
      )}

      {/* Center Sparkline ECharts Container */}
      <div className="telemetry-kpi-sparkline-wrap">
        <div id={`telemetry-kpi-sparkline-${panelId}`} data-chart-id={sparkId} className="telemetry-kpi-sparkline-canvas" />
      </div>

      {/* Bottom Min / Max Indicators */}
      <div className="telemetry-kpi-footer">
        <div className="telemetry-kpi-stat">
          <span className="telemetry-kpi-stat-lbl">MIN</span>
          <span id={`telemetry-kpi-min-${panelId}`} className="telemetry-kpi-stat-val">{minStr}</span>
        </div>
        <div className="telemetry-kpi-stat">
          <span className="telemetry-kpi-stat-lbl">MAX</span>
          <span id={`telemetry-kpi-max-${panelId}`} className="telemetry-kpi-stat-val">{maxStr}</span>
        </div>
      </div>
    </div>
  );
};
